import { useMemo, useRef, useState } from 'react';

import {
  Accordion,
  Bubble,
  Icon,
  Tabs,
} from '@frontendmentorio/design-system-v2';

import Issue from './Issue';

function Heading({ count, isOpen, ...props }) {
  return (
    <h2
      className="cursor-pointer flex justify-between font-secondary items-center p-4 text-xl"
      {...props}
    >
      <span className="flex gap-2 items-center">
        Issues
        <Bubble className="w-[1.8rem]" size="sm">
          {count}
        </Bubble>
      </span>
      <Icon
        className="text-neutral-500 text-sm"
        name={isOpen ? 'chevron-down' : 'chevron-right'}
        style="solid"
      />
    </h2>
  );
}

export default function Issues({
  currentUser,
  isAuthor,
  isOpen,
  onClick,
  report,
  type,
}) {
  const [activeTab, setActiveTab] = useState('all');
  const ref = useRef();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    ref.current?.scrollTo({ top: 0 });
  };

  const issues = useMemo(() => {
    const dict = ['error', 'warning', 'info'].reduce((acc, severity) => {
      acc[severity] = report?.issues?.filter(
        (issue) => issue.severity === severity
      );

      return acc;
    }, {});

    dict.all = report?.issues;

    return dict;
  }, [report?.issues]);

  const includesAI = useMemo(() => {
    return issues?.[activeTab]?.some((issue) => issue.isAI);
  }, [issues, activeTab]);

  return (
    <Accordion
      heading={<Heading count={issues?.all?.length} />}
      isOpen={isOpen}
    >
      <div className="flex flex-col gap-5 items-start p-4">
        <Tabs
          activeTab={activeTab}
          onChange={handleTabChange}
          options={[
            { label: `All (${issues?.all?.length})`, value: 'all' },
            { label: `Error (${issues?.error?.length})`, value: 'error' },
            { label: `Warning (${issues?.warning?.length})`, value: 'warning' },
            { label: `Info (${issues?.info?.length})`, value: 'info' },
          ]}
        />

        {issues?.[activeTab]?.length > 0 ? (
          <>
            <div
              className="flex flex-col gap-5 max-h-[30rem] overflow-y-auto w-full"
              ref={ref}
            >
              {issues?.[activeTab]?.map((issue) => (
                <Issue
                  currentUser={currentUser}
                  handleClick={onClick}
                  issue={issue}
                  isAuthor={isAuthor}
                  key={issue.id}
                />
              ))}
            </div>

            {includesAI && (
              <div className="bg-violet-50 border border-violet-200 flex gap-3 items-start p-3 rounded-lg w-full">
                <Bubble
                  ariaHidden
                  className="bg-violet-600 shrink-0 w-[2.8rem]"
                >
                  <Icon name="exclamation-triangle" style="solid" />
                </Bubble>
                <div className="flex flex-col gap-2">
                  <h4 className="text-violet-600 font-secondary text-lg">
                    AI-detected issues notice
                  </h4>
                  <p className="text-sm">
                    Some issues shown above were detected by AI analysis and may
                    require human review for accuracy. If you’re unsure about
                    any findings or need clarification, please reach out in the
                    help channel on Discord for assistance.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-neutral-50 border border-neutral-200 flex gap-4 items-start p-4 rounded-lg w-full">
            <Bubble ariaHidden className="bg-emerald-400">
              <Icon
                className="text-xl text-neutral-900"
                name="check"
                style="solid"
              />
            </Bubble>

            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-secondary">No issues found</h3>

              <p>
                Great job! There are no {activeTab}-level {type} issues in this
                project.
              </p>
            </div>
          </div>
        )}
      </div>
    </Accordion>
  );
}
